import { gsap } from 'gsap'
import { onMount, onDestroy } from '@/modules/_'

export default function (element: HTMLElement, dataset: DOMStringMap) {
  const els = {
    overlay: element.querySelector('.casestudy_card_overlay') as HTMLElement,
    callout: element.querySelector('.casestudy_card_callout') as HTMLElement,
    details: element.querySelector('.casestudy_card_details') as HTMLElement,
    info: element.querySelector('.casestudy_card_info') as HTMLElement,
  }

  if (!els.overlay || !els.callout || !els.details || !els.info) return

  let tlIn: gsap.core.Timeline | null = null
  let tlOut: gsap.core.Timeline | null = null

  const easeOutQuart = 'cubic-bezier(0.165, 0.84, 0.44, 1)'
  const easeInOutQuart = 'cubic-bezier(0.76, 0, 0.24, 1)'
  const easeInOutCubic = 'cubic-bezier(0.65, 0, 0.35, 1)'
  const easeInOutQuad = 'cubic-bezier(0.45, 0, 0.55, 1)'

  const createHoverInTimeline = () => {
    return gsap
      .timeline({ paused: true })
      .to(
        els.overlay,
        {
          backgroundColor: '#11111280',
          duration: 0.5,
          ease: easeInOutQuad,
        },
        0
      )
      .to(
        els.callout,
        {
          scale: 0.92,
          opacity: 0,
          filter: 'blur(2px)',
          duration: 0.45,
          ease: easeInOutQuart,
          transformOrigin: 'center center',
        },
        0
      )
      .to(
        els.details,
        {
          y: -10,
          opacity: 0,
          filter: 'blur(1px)',
          duration: 0.4,
          ease: easeInOutCubic,
        },
        0
      )
      .fromTo(
        els.info,
        {
          opacity: 0,
          y: 6,
          scale: 0.99,
          filter: 'blur(3px)',
        },
        {
          opacity: 1,
          y: 0,
          scale: 1,
          filter: 'blur(0px)',
          duration: 0.5,
          ease: easeOutQuart,
        },
        0.1
      )
  }

  const createHoverOutTimeline = () => {
    return gsap
      .timeline({ paused: true })
      .to(
        els.overlay,
        {
          backgroundColor: 'transparent',
          duration: 0.65,
          ease: easeInOutQuad,
        },
        0
      )
      .to(
        els.info,
        {
          opacity: 0,
          y: -5,
          scale: 0.99,
          filter: 'blur(2px)',
          duration: 0.45,
          ease: easeInOutCubic,
        },
        0
      )
      .to(
        els.details,
        {
          y: 0,
          opacity: 1,
          filter: 'blur(0px)',
          duration: 0.7,
          ease: easeOutQuart,
        },
        0.15
      )
      .to(
        els.callout,
        {
          scale: 1,
          opacity: 1,
          filter: 'blur(0px)',
          duration: 0.75,
          ease: easeOutQuart,
        },
        0.2
      )
  }

  const handleMouseEnter = () => {
    if (tlOut) tlOut.kill()
    if (!tlIn) tlIn = createHoverInTimeline()
    tlIn.restart()
  }

  const handleMouseLeave = () => {
    if (tlIn) tlIn.kill()
    if (!tlOut) tlOut = createHoverOutTimeline()
    tlOut.restart()
  }

  onMount(() => {
    gsap.set(els.info, { opacity: 0 })

    const mm = gsap.matchMedia()

    mm.add('(hover: hover) and (pointer: fine)', () => {
      element.addEventListener('mouseenter', handleMouseEnter)
      element.addEventListener('mouseleave', handleMouseLeave)

      return () => {
        element.removeEventListener('mouseenter', handleMouseEnter)
        element.removeEventListener('mouseleave', handleMouseLeave)
      }
    })
  })

  onDestroy(() => {
    element.removeEventListener('mouseenter', handleMouseEnter)
    element.removeEventListener('mouseleave', handleMouseLeave)
    tlIn?.kill()
    tlOut?.kill()
  })
}
